import * as React from 'react';
import {observer} from 'mobx-react';
import {center, centerJustified, horizontal, horizontallySpaced, start, vertical} from 'csstips';
import {px} from 'csx';
import {colorSlate} from '../theme/color';
import {classes, style} from 'typestyle';
import {SVGIcon} from './svg-icon';
import {ButtonCircle} from './button-circle';
import {mediaMobileOnly} from '../theme/media';

export interface PlayerBarVolumeProps {
  volume: number
  onChangeVolume(volume: number): void;
}

export interface PlayerBarVolumeState {
  showSlider: boolean;
}

@observer
export class PlayerBarVolume extends React.Component<PlayerBarVolumeProps, PlayerBarVolumeState> {
  private wrapper: HTMLDivElement;

  constructor() {
    super();
    this.state = { showSlider: false };
  }

  render() {
    const { volume } = this.props;
    const { showSlider } = this.state;

    return (
      <label className={classes(PlayerBarVolume.styles.container, showSlider && PlayerBarVolume.styles.containerActive)}>
        <div ref={el => this.wrapper = el} className={classes(PlayerBarVolume.styles.inputWrapper, showSlider && PlayerBarVolume.styles.inputVisible)}>
          <input
            className={PlayerBarVolume.styles.input}
            type="range"
            min="0"
            max="100"
            step="1"
            value={volume.toString()}
            onInput={this.handleChange}
            onChange={this.handleChange}
          />
        </div>
        <ButtonCircle size={46} onClick={this.handleClick}>
          <SVGIcon.Volume size={32} />
        </ButtonCircle>
      </label>
    )
  }

  handleChange = (event: React.FormEvent<any>) => {
    this.props.onChangeVolume(parseInt((event.target as HTMLInputElement).value));
  };

  handleClick = (event: React.MouseEvent<any>) => {
    event.stopPropagation();
    this.setState({ showSlider: !this.state.showSlider });
  };

  handleWindowClick = (event: MouseEvent) => {
    const { showSlider } = this.state;
    if (!showSlider || !this.wrapper) return;

    const target = event.target as HTMLElement;
    if (!this.wrapper.contains(target) && this.wrapper !== target) {
      this.setState({ showSlider: false });
    }
  };

  componentDidMount() {
    window.addEventListener('click', this.handleWindowClick);
  }

  componentWillUnmount() {
    window.removeEventListener('click', this.handleWindowClick)
  }
  
  static styles = {
    container: style({
      ...horizontal,
      ...horizontallySpaced(10),
      ...center,
      position: 'relative',
      padding: '0 12px 0 10px',
    }),
    input: style({
      width: px(80),
      borderRadius: px(4),
      '-webkit-appearance': 'none',
      appearance: 'none',
      margin: '8px 0',
      $nest: {
        '&[type="range"]:focus': {
          outline: 'none'
        },
        '&[type="range"]::-webkit-slider-runnable-track': {
          width: '100%',
          height: '2px',
          cursor: 'pointer',
          boxShadow: '0px 0px 0px #000000, 0px 0px 0px #0d0d0d',
          background: '#ffffff',
          borderRadius: '4px',
          border: '0px solid #562425',
        },
        '&[type="range"]::-webkit-slider-thumb': {
          boxShadow: '0px 0px 0px #050000, 0px 0px 0px #1f0000',
          border: '2px solid #ffffff',
          height: '20px',
          width: '20px',
          borderRadius: '20px',
          background: '#4a00ff',
          cursor: 'pointer',
          '-webkit-appearance': 'none',
          marginTop: '-9px',
        },
        '&[type="range"]:focus::-webkit-slider-runnable-track': {
          background: '#ffffff',
        },
        '&[type="range"]::-moz-range-track': {
          width: '100%',
          height: '4px',
          cursor: 'pointer',
          boxShadow: '0px 0px 0px #000000, 0px 0px 0px #0d0d0d',
          background: '#ffffff',
          borderRadius: '4px',
          border: '0px solid #562425',
        },
        '&[type="range"]::-moz-range-thumb': {
          boxShadow: '0px 0px 0px #050000, 0px 0px 0px #1f0000',
          border: '2px solid #ffffff',
          height: '20px',
          width: '20px',
          borderRadius: '20px',
          background: '#4a00ff',
          cursor: 'pointer',
        },
        '&[type="range"]::-ms-track': {
          width: '100%',
          height: '4px',
          cursor: 'pointer',
          background: 'transparent',
          borderColor: 'transparent',
          color: 'transparent',
        },
        '&[type="range"]::-ms-fill-lower': {
          background: '#f2f2f2',
          border: '0px solid #562425',
          borderRadius: '8px',
          boxShadow: '0px 0px 0px #000000, 0px 0px 0px #0d0d0d',
        },
        '&[type="range"]::-ms-fill-upper': {
          background: '#ffffff',
          border: '0px solid #562425',
          borderRadius: '8px',
          boxShadow: '0px 0px 0px #000000, 0px 0px 0px #0d0d0d',
        },
        '&[type="range"]::-ms-thumb': {
          boxShadow: '0px 0px 0px #050000, 0px 0px 0px #1f0000',
          border: '2px solid #ffffff',
          height: '20px',
          width: '20px',
          borderRadius: '20px',
          background: '#4a00ff',
          cursor: 'pointer',
        },
        '&[type="range"]:focus::-ms-fill-lower': {
          background: '#ffffff',
        },
        '&[type="range"]:focus::-ms-fill-upper': {
          background: '#ffffff',
        },
      }
    }),
    containerActive: style(
      mediaMobileOnly({
        background: colorSlate.toString(),
        zIndex: 7,
      })
    ),
    inputWrapper: style(
      {
        ...vertical,
        ...centerJustified,
        ...start,
        display: 'flex',
        padding: '0 0 0 4px'
      },
      mediaMobileOnly({
        display: 'none',
        padding: 0
      })
    ),
    inputVisible: style(
      mediaMobileOnly({
        display: 'flex',
        position: 'absolute',
        bottom: px(50),
        background: colorSlate.toString(),
        right: px(10),
        height: px(70),
        width: px(100),
        transform: 'rotate(270deg)',
        transformOrigin: 'top'
      })
    )
  }
}